// @ts-nocheck
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useEffect, useMemo, useCallback } from 'react';

export interface NavigationItem {
  id: string;
  path: string;
  label: string;
  description?: string;
  icon?: any;
  color?: 'purple' | 'blue' | 'green' | 'orange' | 'yellow' | 'red';
  requiresPermission?: boolean;
  rootAdminOnly?: boolean;
}

export interface UseContextNavigationProps {
  contextKey: 'admin' | 'organization' | 'settings' | 'help';
  navigationItems: NavigationItem[];
  defaultPath?: string;
}

export function useContextNavigation({ 
  contextKey, 
  navigationItems, 
  defaultPath 
}: UseContextNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSubContextState, getSubContextState } = useSidebar();

  // Get current active navigation item based on pathname
  // Sort by path length (descending) to match more specific paths first
  const activeItem = useMemo(() => {
    const sortedItems = [...navigationItems].sort((a, b) => b.path.length - a.path.length);
    return sortedItems.find(item =>
      location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path + '/'))
    );
  }, [navigationItems, location.pathname]);
  if (typeof window !== 'undefined') {
    console.log('🧭 useContextNavigation: computed activeItem', {
      contextKey,
      pathname: location.pathname,
      activeItem: activeItem?.id,
      activeItemPath: activeItem?.path
    });
  }

  // Save current path to sub-context state when it changes
  useEffect(() => {
    if (activeItem) {
      setSubContextState(contextKey, activeItem.path);
    }
  }, [activeItem, contextKey, setSubContextState]);

  // Navigation handler that preserves context
  const handleNavigation = useCallback((item: NavigationItem) => {
    setSubContextState(contextKey, item.path);
    if (location.pathname !== item.path) {
      navigate(item.path);
    }
  }, [contextKey, navigate, setSubContextState, location.pathname]);

  // Get restored path when context is activated
  const getRestoredPath = (): string => {
    const savedPath = getSubContextState(contextKey);
    
    // If we have a saved path and it's valid, return it
    if (savedPath && navigationItems.some(item => item.path === savedPath)) {
      return savedPath;
    }
    
    // Otherwise return default path or first item
    return defaultPath || navigationItems[0]?.path || '/';
  };

  // Check if a specific path is active
  const isPathActive = useCallback((path: string): boolean => {
    return location.pathname === path ||
           (path !== '/' && location.pathname.startsWith(path + '/'));
  }, [location.pathname]);

  // Get button styles for navigation items
  const getButtonStyles = (item: NavigationItem, baseStyles: string = ''): string => {
    const isActive = isPathActive(item.path);
    const color = item.color || 'purple';
    
    // Use specific color classes to ensure they're included in build
    const colorMap = {
      purple: {
        active: 'border-purple-400/50 bg-purple-600/10',
        hover: 'hover:bg-purple-600/10 hover:border-purple-400/50',
        text: 'text-purple-400 hover:text-purple-300'
      },
      blue: {
        active: 'border-blue-400/50 bg-blue-600/10',
        hover: 'hover:bg-blue-600/10 hover:border-blue-400/50',
        text: 'text-blue-400 hover:text-blue-300'
      },
      green: {
        active: 'border-green-400/50 bg-green-600/10',
        hover: 'hover:bg-green-600/10 hover:border-green-400/50',
        text: 'text-green-400 hover:text-green-300'
      },
      orange: {
        active: 'border-orange-400/50 bg-orange-600/10',
        hover: 'hover:bg-orange-600/10 hover:border-orange-400/50',
        text: 'text-orange-400 hover:text-orange-300'
      },
      yellow: {
        active: 'border-yellow-400/50 bg-yellow-600/10',
        hover: 'hover:bg-yellow-600/10 hover:border-yellow-400/50',
        text: 'text-yellow-400 hover:text-yellow-300'
      }
    };
    
    const colors = colorMap[color] || colorMap.purple;
    
    return `${baseStyles} ${colors.hover} border transition-all text-left ${colors.text} ${
      isActive ? colors.active : 'border-transparent'
    }`;
  };

  return {
    activeItem,
    handleNavigation,
    getRestoredPath,
    isPathActive,
    getButtonStyles,
    currentPath: location.pathname
  };
}

// Default navigation configurations for different contexts
export const ADMIN_NAVIGATION: NavigationItem[] = [
  {
    id: 'users',
    path: '/admin/users',
    label: 'Users',
    description: 'Manage system administrators',
    color: 'blue'
  },
  {
    id: 'organizations',
    path: '/admin/organizations',
    label: 'Organizations',
    description: 'Manage organizations and limits',
    color: 'green'
  },
  {
    id: 'prompts',
    path: '/admin/prompts',
    label: 'Global Prompts',
    description: 'Manage system-wide prompts',
    color: 'purple'
  },
  {
    id: 'prompts-v2',
    path: '/admin/prompts-v2',
    label: 'References',
    description: 'Manage references with categories',
    color: 'purple'
  },
  {
    id: 'prompt-categories',
    path: '/admin/prompt-categories',
    label: 'Reference Categories',
    description: 'Manage reference categories and subcategories',
    color: 'purple'
  },
  {
    id: 'widgets',
    path: '/admin/widgets',
    label: 'Widgets',
    description: 'Manage widget types and display settings',
    color: 'orange'
  },
  {
    id: 'workflows',
    path: '/admin/workflows',
    label: 'Workflows',
    description: 'Manage AI generation workflows',
    color: 'yellow'
  },
  {
    id: 'debug',
    path: '/admin/debug',
    label: 'Debug Tools',
    description: 'System maintenance and diagnostics',
    color: 'orange'
  },
  {
    id: 'billing',
    path: '/admin/billing',
    label: 'Billing',
    description: 'View payment transactions',
    color: 'green'
  },
  {
    id: 'refunds',
    path: '/admin/refunds',
    label: 'All Refunds',
    description: 'Refunded tokens across all orgs',
    color: 'red'
  },
  {
    id: 'packages',
    path: '/admin/packages',
    label: 'Token Packages',
    description: 'Manage token bundle packages',
    color: 'green'
  },
  {
    id: 'api-keys',
    path: '/admin/api-keys',
    label: 'Service Keys',
    description: 'Service API keys for machine access',
    color: 'blue',
    rootAdminOnly: true
  }
];

export const ORGANIZATION_NAVIGATION: NavigationItem[] = [
  {
    id: 'info',
    path: '/organization/info',
    label: 'Organization Info',
    description: 'View details and statistics',
    color: 'blue'
  },
  {
    id: 'manager',
    path: '/organization/manager',
    label: 'Management',
    description: 'Manage users and settings',
    color: 'green',
    requiresPermission: true
  },
  {
    id: 'prompts',
    path: '/organization/manager/prompts',
    label: 'Prompts',
    description: 'Manage organization prompts',
    color: 'purple',
    requiresPermission: true
  },
  {
    id: 'history',
    path: '/organization/manager/history',
    label: 'History',
    description: 'View generation history',
    color: 'orange'
  }
];